import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import api from '../api';
import { Team, Game } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { parseGameTime } from '../utils/gameTime';

const fmtShort = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// ── Input style ───────────────────────────────────────────────────────────────
const cellInput: React.CSSProperties = {
  width: '52px', padding: '4px', borderRadius: '6px', border: '1px solid var(--hairline)',
  background: 'var(--surface-sunken)', color: 'var(--ink-1)', fontSize: '13px', textAlign: 'center', fontWeight: 700,
};

// ── Section heading ───────────────────────────────────────────────────────────
const SectionHead = ({ label }: { label: string }) => (
  <div style={{
    fontSize: '11px', fontWeight: 700, letterSpacing: '1.2px', color: 'var(--ink-4)',
    textTransform: 'uppercase', marginBottom: '14px',
  }}>{label}</div>
);

// ── Card wrapper ──────────────────────────────────────────────────────────────
const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12,
    padding: 20, marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', ...style,
  }}>
    {children}
  </div>
);

// ── EditStats page ────────────────────────────────────────────────────────────
const EditStats = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { isAdmin, isTeamManager, managedTeamIds } = useAuth();
  const navigate = useNavigate();

  const [team, setTeam]       = useState<Team | null>(null);
  const [allGames, setAllGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;
    Promise.all([
      api.get(`/teams/${teamId}`),
      api.get(`/games?team_id=${teamId}`),
    ]).then(([tr, gr]) => {
      setTeam(tr.data);
      setAllGames(gr.data);
    }).finally(() => setLoading(false));
  }, [teamId]);

  // access guard (also enforced by ManagerRoute in App.tsx)
  if (!isAdmin && !(isTeamManager && managedTeamIds.includes(teamId ?? ''))) {
    return <Navigate to="/teams" replace />;
  }

  if (loading || !team) {
    return (
      <div className="container" style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-4)' }}>
        Loading…
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '900px', padding: '20px 16px 60px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(`/teams/${teamId}`)}
          className="btn btn-ghost btn-sm"
        >
          <i className="ti ti-arrow-left" /> Back
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: 'var(--ink-1)' }}>
            Edit Stats — {team.name}
          </h2>
          <div style={{ color: 'var(--ink-4)', fontSize: '13px', marginTop: '2px' }}>
            Override team record and edit player stats by game.
          </div>
        </div>
      </div>

      <AppearancePanel team={team} onTeamUpdated={setTeam} />
      <RecordPanel team={team} onTeamUpdated={setTeam} />
      <PlayerStatsPanel team={team} allGames={allGames} onGamesUpdated={setAllGames} />
    </div>
  );
};

// ── Appearance Panel ──────────────────────────────────────────────────────────
const AppearancePanel = ({ team, onTeamUpdated }: { team: Team; onTeamUpdated: (t: Team) => void }) => {
  const [primary,   setPrimary]   = useState(team.primary_color   || '#22C55E');
  const [secondary, setSecondary] = useState(team.secondary_color || '#ffffff');
  const [theme,     setTheme]     = useState<'dark' | 'light'>(team.background_theme || 'dark');
  const [logoFile,  setLogoFile]  = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState(team.logo_url || '');
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState('');

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await api.put(`/teams/${team.id}`, { primary_color: primary, secondary_color: secondary, background_theme: theme });

      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        fd.append('team_id', team.id);
        const lr = await api.post('/videos/upload-team-logo', fd);
        setLogoPreview(lr.data.logo_url);
        setLogoFile(null);
      }

      const r = await api.get(`/teams/${team.id}`);
      onTeamUpdated(r.data);
      setMsg('Saved!');
    } catch { setMsg('Save failed.'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <SectionHead label="Team Appearance" />

      {/* Logo */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '12px', color: 'var(--ink-4)', fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Team Logo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ width: 72, height: 72, borderRadius: 12, border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {logoPreview
              ? <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <i className="ti ti-photo" style={{ fontSize: 28, color: 'var(--ink-4)' }} />}
          </div>
          <label className="btn btn-secondary btn-sm" style={{ cursor: busy ? 'default' : 'pointer' }}>
            <i className="ti ti-upload" /> {logoFile ? logoFile.name : 'Choose logo'}
            <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy} onChange={handleLogoChange} />
          </label>
          <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>JPG, PNG, SVG · max 10 MB</span>
        </div>
      </div>

      {/* Colors */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: 'var(--ink-4)', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Team Colors</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 380 }}>
          {[
            { label: 'Primary', val: primary, set: setPrimary },
            { label: 'Secondary', val: secondary, set: setSecondary },
          ].map(({ label, val, set }) => (
            <div key={label}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 6 }}>{label}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={val} onChange={e => set(e.target.value)}
                  style={{ width: 40, height: 36, padding: 2, background: 'none', border: '1px solid var(--hairline)', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }} />
                <input className="form-control" value={val} onChange={e => set(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
              </div>
            </div>
          ))}
        </div>
        {/* Preview strip */}
        <div style={{ marginTop: 10, borderRadius: 8, overflow: 'hidden', height: 28, display: 'flex', maxWidth: 380 }}>
          <div style={{ flex: 1, background: primary }} />
          <div style={{ flex: 1, background: secondary }} />
          <div style={{ flex: 2, background: 'var(--bg-page)' }} />
        </div>
      </div>

      {/* Background theme — removed since app is now consistently light-themed */}
      <div style={{ marginBottom: 20, display: 'none' }}>
        {/* Theme selector deprecated — app uses unified light theme */}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button className="btn btn-primary" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save Appearance'}
        </button>
        {msg && <span style={{ fontSize: '13px', color: msg.includes('fail') ? '#EF4444' : '#22C55E' }}>{msg}</span>}
      </div>
    </Card>
  );
};

// ── Team Record Panel ─────────────────────────────────────────────────────────
const RecordPanel = ({ team, onTeamUpdated }: { team: Team; onTeamUpdated: (t: Team) => void }) => {
  const rec = (team as any).record as { wins: number; losses: number; ties: number; override?: boolean } | undefined;
  const [recW, setRecW] = useState(String(rec?.wins  ?? 0));
  const [recL, setRecL] = useState(String(rec?.losses ?? 0));
  const [recT, setRecT] = useState(String(rec?.ties   ?? 0));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState('');

  const save = async (clear = false) => {
    setBusy(true); setMsg('');
    try {
      await api.put(`/teams/${team.id}/record`, clear
        ? { wins: null, losses: null, ties: null }
        : { wins: Number(recW), losses: Number(recL), ties: Number(recT) });
      const r = await api.get(`/teams/${team.id}`);
      onTeamUpdated(r.data);
      setMsg(clear ? 'Cleared — using auto-calculated record.' : 'Saved!');
    } catch { setMsg('Save failed.'); }
    finally { setBusy(false); }
  };

  const fields = [
    { label: 'Wins',   val: recW, set: setRecW, color: '#22C55E' },
    { label: 'Losses', val: recL, set: setRecL, color: '#EF4444' },
    { label: 'Ties',   val: recT, set: setRecT, color: '#F97316' },
  ];

  return (
    <Card>
      <SectionHead label="Team Record Override" />
      <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '16px' }}>
        Override the automatically calculated W/L/T record shown on the stats page.
        {rec?.override && <span style={{ color: 'var(--sr-amber)', marginLeft: '8px' }}>Override active</span>}
      </div>

      {/* W / L / T inputs — stacked grid so they never overflow on iPhone */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', maxWidth: '280px', marginBottom: '16px' }}>
        {fields.map(({ label, val, set, color }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#8892A4', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase' }}>{label}</div>
            <input
              type="number" min="0" value={val}
              onChange={e => set(e.target.value)}
              style={{ width: '100%', padding: '10px 4px', borderRadius: '8px', border: '1px solid #252B38', background: '#0D111A', color, fontSize: '20px', textAlign: 'center', fontWeight: 900, boxSizing: 'border-box' }}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => save(false)}>
          {busy ? 'Saving…' : 'Save Record'}
        </button>
        {rec?.override && (
          <button className="btn btn-secondary" disabled={busy} onClick={() => save(true)}>
            Clear Override
          </button>
        )}
      </div>
      {msg && <div style={{ marginTop: '10px', fontSize: '13px', color: msg.includes('fail') ? '#EF4444' : '#22C55E' }}>{msg}</div>}
    </Card>
  );
};

// ── Player Stats Panel ────────────────────────────────────────────────────────
const PlayerStatsPanel = ({ team, allGames, onGamesUpdated }: {
  team: Team; allGames: Game[]; onGamesUpdated: (g: Game[]) => void;
}) => {
  const isBaseball = team.sport === 'baseball' || team.sport === 'softball';

  const completedGames = [...allGames]
    .filter(g => g.status === 'completed' || g.status === 'live')
    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());

  const [selectedGameId, setSelectedGameId] = useState(completedGames[0]?.id ?? '');
  const [gameStats, setGameStats]           = useState<any[]>([]);
  const [statsLoading, setStatsLoading]     = useState(false);
  const [edits, setEdits]                   = useState<Record<string, Record<string, string>>>({});
  const [saving, setSaving]                 = useState<Record<string, boolean>>({});
  const [saved,  setSaved]                  = useState<Record<string, boolean>>({});
  const [deleteConfirm, setDeleteConfirm]   = useState(false);
  const [deleting, setDeleting]             = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importMsg, setImportMsg]           = useState('');

  useEffect(() => {
    if (!selectedGameId) return;
    setStatsLoading(true); setEdits({});
    api.get(`/teams/${team.id}/game-stats/${selectedGameId}`)
      .then(r => setGameStats(r.data))
      .catch(() => setGameStats([]))
      .finally(() => setStatsLoading(false));
  }, [selectedGameId, team.id]);

  const setField = (pid: string, field: string, val: string) =>
    setEdits(prev => ({ ...prev, [pid]: { ...prev[pid], [field]: val } }));

  const getValue = (p: any, field: string) =>
    edits[p.id]?.[field] !== undefined ? edits[p.id][field] : String(p[field] ?? 0);

  const savePlayer = async (p: any) => {
    setSaving(s => ({ ...s, [p.id]: true }));
    const fields = edits[p.id] || {};
    const payload: Record<string, number> = {};
    Object.keys(fields).forEach(k => { payload[k] = Number(fields[k]) || 0; });
    const allFields = isBaseball
      ? ['at_bats','hits','runs','rbis','doubles','triples','home_runs','walks','strikeouts','stolen_bases','innings_pitched','earned_runs','pitching_strikeouts','pitching_walks','errors']
      : ['goals','assists','yellow_cards','red_cards','minutes_played','shots','shots_on_target'];
    allFields.forEach(f => { if (payload[f] === undefined) payload[f] = Number(p[f] ?? 0); });
    payload.season_year = new Date(allGames.find(g => g.id === selectedGameId)?.scheduled_at || '').getFullYear() || new Date().getFullYear();
    try {
      await api.put(`/players/${p.id}/stats/${selectedGameId}`, payload);
      setGameStats(gs => gs.map(r => r.id === p.id ? { ...r, ...payload } : r));
      setEdits(prev => { const n = { ...prev }; delete n[p.id]; return n; });
      setSaved(s => ({ ...s, [p.id]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [p.id]: false })), 2000);
    } catch {}
    finally { setSaving(s => ({ ...s, [p.id]: false })); }
  };

  const saveAll = async () => {
    for (const p of gameStats.filter(p => edits[p.id])) await savePlayer(p);
  };

  const importGameChanger = async (file: File) => {
    if (!selectedGameId) return;
    setImporting(true); setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/teams/${team.id}/import-gamechanger/${selectedGameId}`, fd);
      const { matched, unmatched } = r.data as { matched: any[]; unmatched: any[] };

      // Pre-fill edits for matched players (user must still Save to commit)
      const newEdits: Record<string, Record<string, string>> = { ...edits };
      matched.forEach(m => {
        const fields: Record<string, string> = {};
        ['at_bats','hits','doubles','triples','home_runs','rbis','runs','walks','strikeouts',
         'stolen_bases','innings_pitched','earned_runs','pitching_strikeouts','pitching_walks','errors']
          .forEach(k => { fields[k] = String(m[k] ?? 0); });
        newEdits[m.player_id] = fields;
      });
      setEdits(newEdits);

      const msgs: string[] = [`Imported ${matched.length} player${matched.length !== 1 ? 's' : ''}.`];
      if (unmatched.length) msgs.push(`${unmatched.length} name${unmatched.length !== 1 ? 's' : ''} not matched to roster: ${unmatched.map(u => u.extracted_name).join(', ')}.`);
      msgs.push('Review the pre-filled stats below, then click Save.');
      setImportMsg(msgs.join(' '));
    } catch (err: any) {
      setImportMsg(err.response?.data?.error || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const deleteGame = async () => {
    setDeleting(true);
    try {
      await api.delete(`/games/${selectedGameId}`);
      const remaining = allGames.filter(g => g.id !== selectedGameId);
      onGamesUpdated(remaining);
      const next = remaining.filter(g => g.status === 'completed' || g.status === 'live')[0];
      setSelectedGameId(next?.id ?? '');
      setDeleteConfirm(false);
    } catch { alert('Failed to delete game.'); }
    finally { setDeleting(false); }
  };

  const soccerCols = [
    { key: 'goals',           label: 'Goals' },
    { key: 'assists',         label: 'Assists' },
    { key: 'yellow_cards',    label: 'YC' },
    { key: 'red_cards',       label: 'RC' },
    { key: 'minutes_played',  label: 'Min' },
    { key: 'shots',           label: 'Shots' },
    { key: 'shots_on_target', label: 'SOT' },
  ];
  const baseballCols = [
    { key: 'at_bats',             label: 'AB' },
    { key: 'hits',                label: 'H' },
    { key: 'doubles',             label: '2B' },
    { key: 'triples',             label: '3B' },
    { key: 'home_runs',           label: 'HR' },
    { key: 'rbis',                label: 'RBI' },
    { key: 'runs',                label: 'R' },
    { key: 'walks',               label: 'BB' },
    { key: 'strikeouts',          label: 'K' },
    { key: 'stolen_bases',        label: 'SB' },
    { key: 'innings_pitched',     label: 'IP' },
    { key: 'earned_runs',         label: 'ER' },
    { key: 'pitching_strikeouts', label: 'PK' },
    { key: 'pitching_walks',      label: 'PBB' },
    { key: 'errors',              label: 'E' },
  ];
  const statCols = isBaseball ? baseballCols : soccerCols;
  const hasDirty = gameStats.some(p => edits[p.id] && Object.keys(edits[p.id]).length > 0);

  if (completedGames.length === 0) {
    return (
      <Card>
        <SectionHead label="Player Stats by Game" />
        <div style={{ color: '#4A5568', fontSize: '13px' }}>No completed games yet.</div>
      </Card>
    );
  }

  const selectedGame = completedGames.find(g => g.id === selectedGameId);

  return (
    <Card>
      <SectionHead label="Player Stats by Game" />

      {/* Game picker + actions */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          className="form-control"
          value={selectedGameId}
          onChange={e => { setSelectedGameId(e.target.value); setDeleteConfirm(false); }}
          style={{ flex: '1 1 240px', minWidth: 0 }}
        >
          {completedGames.map(g => (
            <option key={g.id} value={g.id}>
              {fmtShort(g.scheduled_at)} — {g.title}
            </option>
          ))}
        </select>
        {hasDirty && (
          <button className="btn btn-primary btn-sm" onClick={saveAll}>Save All</button>
        )}
      </div>

      {/* GameChanger import — baseball/softball only */}
      {isBaseball && selectedGameId && (
        <div style={{ marginBottom: '16px', padding: '12px 14px', background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#F97316', marginBottom: '4px' }}>
            Import from GameChanger
          </div>
          <div style={{ fontSize: '12px', color: '#4A5568', marginBottom: '10px' }}>
            Upload a screenshot or PDF of the box score from GameChanger. Stats will be pre-filled below — review and save when ready.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{
              cursor: importing ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(249,115,22,0.4)',
              background: importing ? '#252B38' : 'rgba(249,115,22,0.12)', color: importing ? '#4A5568' : '#F97316',
              fontSize: '13px', fontWeight: 700,
            }}>
              {importing ? '⏳ Extracting stats…' : '📤 Choose file'}
              <input
                type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                disabled={importing}
                onChange={e => { const f = e.target.files?.[0]; if (f) importGameChanger(f); e.target.value = ''; }}
              />
            </label>
            <span style={{ fontSize: '11px', color: '#4A5568' }}>JPG, PNG, PDF</span>
          </div>
          {importMsg && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: importMsg.includes('fail') || importMsg.includes('Error') ? '#F87171' : '#22C55E', lineHeight: 1.5 }}>
              {importMsg}
            </div>
          )}
        </div>
      )}

      {/* Delete game section */}
      {selectedGame && (
        <div style={{ marginBottom: '20px', padding: '12px 14px', background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '10px' }}>
          {!deleteConfirm ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#8892A4' }}>
                Delete this game permanently (cannot be undone)
              </span>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setDeleteConfirm(true)}
              >
                Delete Game
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#EF4444', fontWeight: 700 }}>
                Delete "{selectedGame.title}"? This cannot be undone.
              </span>
              <button className="btn btn-danger btn-sm" disabled={deleting} onClick={deleteGame}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stats table */}
      {statsLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#4A5568' }}>Loading…</div>
      ) : gameStats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#4A5568' }}>No players on roster.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #252B38' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px 8px 0', color: '#4A5568', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>Player</th>
                {statCols.map(c => (
                  <th key={c.key} style={{ padding: '6px 4px 8px', color: '#4A5568', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', textAlign: 'center', whiteSpace: 'nowrap' }}>{c.label}</th>
                ))}
                <th style={{ width: '70px' }} />
              </tr>
            </thead>
            <tbody>
              {gameStats.map((p, i) => {
                const isDirty = !!(edits[p.id] && Object.keys(edits[p.id]).length > 0);
                return (
                  <tr key={p.id} style={{ borderBottom: i < gameStats.length - 1 ? '1px solid rgba(37,43,56,0.6)' : 'none', background: isDirty ? 'rgba(249,115,22,0.04)' : 'transparent' }}>
                    <td style={{ padding: '8px 10px 8px 0', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 700, color: '#F0F6FC' }}>{p.name}</span>
                      {p.number && <span style={{ color: '#4A5568', fontSize: '11px', marginLeft: '6px' }}>#{p.number}</span>}
                    </td>
                    {statCols.map(c => (
                      <td key={c.key} style={{ padding: '5px 3px', textAlign: 'center' }}>
                        <input
                          type="number" min="0"
                          value={getValue(p, c.key)}
                          onChange={e => setField(p.id, c.key, e.target.value)}
                          style={cellInput}
                        />
                      </td>
                    ))}
                    <td style={{ padding: '5px 0 5px 6px', whiteSpace: 'nowrap' }}>
                      {saved[p.id] ? (
                        <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 700 }}>✓</span>
                      ) : (
                        <button
                          className="btn btn-sm"
                          disabled={saving[p.id] || !isDirty}
                          onClick={() => savePlayer(p)}
                          style={{ opacity: isDirty ? 1 : 0.3, background: isDirty ? 'rgba(249,115,22,0.12)' : 'transparent', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                        >
                          {saving[p.id] ? '…' : 'Save'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default EditStats;
